import { Action, ThunkAction } from '@reduxjs/toolkit';
import { modules } from '@veritone/glc-redux';
import { v4 as uuid } from 'uuid';
import callGraphQLApi, {
  GQLApiDispatch,
  GQLApiGetState,
} from '@helpers/callGraphQLApi';
import { fetchGraphQLApi } from '@helpers/callApi';
import {
  clearRedactionStatusSchemaId,
  lookupLatestRedactionStatusSchemaId,
} from '@cbsa-modules/universal';
import {
  DELETE_REDACTION_CUSTOM_STATUS_FAILURE,
  DELETE_REDACTION_CUSTOM_STATUS_SUCCESS,
  FETCH_REDACTION_STATUSES_FAILURE,
  FETCH_REDACTION_STATUSES_SUCCESS,
  REORDER_REDACTION_CUSTOM_STATUSES_FAILURE,
  REORDER_REDACTION_CUSTOM_STATUSES_SUCCESS,
  RESTORE_REDACTION_CUSTOM_STATUS_FAILURE,
  RESTORE_REDACTION_CUSTOM_STATUS_SUCCESS,
  SAVE_REDACTION_CUSTOM_STATUS_FAILURE,
  SAVE_REDACTION_CUSTOM_STATUS_SUCCESS,
  SAVE_REDACTION_STATUSES_FAILURE,
  SAVE_REDACTION_STATUSES_SUCCESS,
} from '../actions';
import {
  FetchRedactionStatusesResponse,
  RedactionCustomStatus,
  RedactionStatusSdoRecord,
  getNextCustomStatusDisplayOrder,
  getRedactionCustomStatusSdoData,
  getRedactionCustomStatusesFromRecords,
  getRedactionStatusSdoData,
  normalizeRedactionStatusSdoRecord,
} from '../models';
import {
  DeleteRedactionStatusResponse,
  RETRIEVE_REDACTION_STATUSES,
  SaveRedactionStatusResponse,
  permanentlyDeleteRedactionStatusQuery,
  saveRedactionStatusQuery,
} from './queries';
import {
  RedactionStatusFilter,
  RedactionStatusSettings,
} from '@common-modules/mediaDetails/redactionStatuses';
import {
  selectRedactionCustomStatuses,
  selectRedactionStatusSettings,
} from '../selectors';

const {
  auth: { selectOAuthToken, selectSessionToken },
  config: { getConfig },
  user: userModule,
} = modules;

const NOOP = 'noop';

/**
 * One chain for every status write (VE-24546 #4). `takeLatest` cancels the saga
 * worker, not the thunk it already dispatched, so without this two rapid
 * actions — an Enabled toggle double-tap, a drag landing on a rename — race
 * their GraphQL writes and whichever finishes last wins, which is not
 * necessarily the newest intent.
 */
let statusPersistChain: Promise<unknown> = Promise.resolve();

/**
 * How long a queued write waits on its predecessor before going anyway.
 *
 * `fetchGraphQLApi` has no timeout or AbortController, so a stalled connection
 * leaves a write pending indefinitely. Without this bound that one request
 * would block every later status write for the rest of the session, and
 * `isSavingStatuses` — which the Statuses page and drawer disable themselves on
 * — would never clear, silently turning the page read-only. Losing write
 * ordering after 30s is the better failure.
 */
const STATUS_PERSIST_QUEUE_TIMEOUT_MS = 30_000;

const enqueueStatusPersist = async <T>(run: () => Promise<T>): Promise<T> => {
  // Chain on the prior run's settle so one failure doesn't stall the queue.
  const previous = statusPersistChain;
  const gate = new Promise<void>((resolve) => {
    // Cleared as soon as the predecessor settles — a bare Promise.race would
    // leave one live 30s timer behind per write, holding the event loop open.
    const timer = setTimeout(resolve, STATUS_PERSIST_QUEUE_TIMEOUT_MS);
    void previous
      .catch(() => undefined)
      .then(() => {
        clearTimeout(timer);
        resolve();
        return undefined;
      });
  });
  const next = gate.then(run);
  statusPersistChain = next.catch(() => undefined);
  return await next;
};

type Thunk<P, R = void> = (
  payload: P
) => ThunkAction<Promise<R | undefined>, any, unknown, Action>;

interface SaveRedactionStatusesPayload {
  readonly redactionStatuses: RedactionStatusSettings;
  readonly sdoIdsByStatus: Record<string, string>;
  readonly changedStatusFilter?: RedactionStatusFilter;
  readonly silent?: boolean;
}

/**
 * Monotonic id per fetch, so a response that arrives after a newer fetch has
 * started is dropped instead of applied (VE-24546). `takeLatest` on the saga
 * would not close this: the worker dispatches this thunk and finishes, so
 * cancelling it leaves the older request in flight and its SUCCESS still lands
 * — and, being older, lands last after two quick reorders.
 */
let fetchSequence = 0;

const STATUS_FETCH_PAGE_SIZE = 100;
/** Backstop so a bad `count` can't spin the pager forever. */
const STATUS_FETCH_MAX_PAGES = 20;

export const fetchRedactionStatuses =
  () => async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const requestId = ++fetchSequence;
    const dispatchIfCurrent: GQLApiDispatch = (action) => {
      if (requestId !== fetchSequence) {
        return;
      }
      return dispatch(action);
    };

    const schemaId = await lookupLatestRedactionStatusSchemaId(
      dispatch,
      getState
    );
    if (!schemaId) {
      dispatch({
        type: FETCH_REDACTION_STATUSES_FAILURE.type,
        payload: undefined,
        error: true,
        meta: {
          variables: {},
          operationName: 'lookupLatestRedactionStatusSchemaId',
          query: 'lookupLatestRedactionStatusSchemaId',
          _internalRequestId: 'redaction-statuses-missing-schema-id',
          _shouldTrackRequestsIndividually: false,
        },
      });
      return;
    }

    // Page through the whole set: a single limit:100 call silently dropped any
    // status past the hundredth, and files tagged with a dropped status render
    // as "Unknown" pills (VE-24546).
    const records: RedactionStatusSdoRecord[] = [];
    let truncated = false;

    for (let page = 0; page < STATUS_FETCH_MAX_PAGES; page += 1) {
      let pageData: FetchRedactionStatusesResponse | undefined;
      // Capture the page rather than reducing it — the store should only ever
      // see the complete list, never a partial first page. Failures still go
      // through, subject to the staleness guard.
      const capturePage: GQLApiDispatch = (action) => {
        if (action.type === FETCH_REDACTION_STATUSES_SUCCESS.type) {
          pageData = action.payload as FetchRedactionStatusesResponse;
          return;
        }
        return dispatchIfCurrent(action);
      };

      await callGraphQLApi<FetchRedactionStatusesResponse>({
        actionTypes: [
          NOOP,
          FETCH_REDACTION_STATUSES_SUCCESS,
          FETCH_REDACTION_STATUSES_FAILURE,
        ],
        query: RETRIEVE_REDACTION_STATUSES,
        variables: {
          schemaId,
          limit: STATUS_FETCH_PAGE_SIZE,
          offset: records.length,
        },
        dispatch: capturePage,
        getState,
      });

      if (!pageData) {
        // callGraphQLApi returned without a success payload, so it already
        // dispatched FAILURE and swallowed the error rather than throwing —
        // it does exactly that for `not_found`. Synthesizing a SUCCESS below
        // would clear customStatuses and hand the seeding path an empty
        // record set, which overwrites the org's real settings with the
        // built-in defaults. Stop here and leave the FAILURE as the outcome.
        return;
      }

      // A short page is the end of the set. Deliberately NOT
      // `records.length >= count`: `structuredDataObjects.count` is the page
      // count in some callers here and arguably the total in others, and the
      // API schema was not reachable to settle it — this rule is correct
      // under either reading.
      const pageRecords = pageData.structuredDataObjects?.records ?? [];
      records.push(...pageRecords);
      if (pageRecords.length < STATUS_FETCH_PAGE_SIZE) {
        break;
      }
      truncated = page === STATUS_FETCH_MAX_PAGES - 1;
    }

    if (truncated) {
      console.warn(
        `fetchRedactionStatuses stopped at the ${STATUS_FETCH_MAX_PAGES}-page cap with ${records.length} statuses; there may be more.`
      );
    }

    if (requestId !== fetchSequence) {
      return;
    }
    const structuredDataObjects = {
      count: records.length,
      records,
    };
    dispatch(
      FETCH_REDACTION_STATUSES_SUCCESS({ structuredDataObjects }, undefined)
    );
    return { structuredDataObjects };
  };

export const saveRedactionStatuses: Thunk<SaveRedactionStatusesPayload> =
  (payload) => async (dispatch, getState) =>
    await enqueueStatusPersist(async () => {
      await persistRedactionStatuses(payload, dispatch, getState);
    });

const persistRedactionStatuses = async (
  payload: SaveRedactionStatusesPayload,
  dispatch: GQLApiDispatch,
  getState: GQLApiGetState
) => {
  const { redactionStatuses, sdoIdsByStatus, changedStatusFilter, silent } =
    payload;
  const resolveSchemaId = async (ignorePinnedSchemaId = false) =>
    await lookupLatestRedactionStatusSchemaId(dispatch, getState, {
      ignorePinnedSchemaId,
    });

  const oauthToken = selectOAuthToken(getState());
  const sessionToken = selectSessionToken(getState());
  const { apiRoot, graphQLEndpoint, veritoneAppId } =
    getConfig<Window['config']>(getState());
  const authorization = {
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    token: sessionToken || oauthToken,
    veritoneAppId,
  };

  const saveWithSchemaId = async (schemaId: string) => {
    const statusesToSave = getRedactionStatusSdoData(redactionStatuses);
    const filteredStatuses = changedStatusFilter
      ? statusesToSave.filter(
          (status) => status.statusKey === changedStatusFilter
        )
      : statusesToSave;

    const records = await Promise.all(
      filteredStatuses.map(async (status) => {
        const lookupKey = status.statusKey;
        const id = (lookupKey && sdoIdsByStatus[lookupKey]) || uuid();
        const response = await fetchGraphQLApi<SaveRedactionStatusResponse>({
          ...authorization,
          query: saveRedactionStatusQuery,
          variables: {
            id,
            schemaId,
            data: status,
          },
        });
        if (response?.errors?.length) {
          const firstError = response.errors[0] as
            | { message?: string }
            | undefined;
          const message =
            firstError?.message ||
            'Unknown GraphQL error while saving redaction status.';
          throw new Error(message);
        }
        const saved = response?.data?.createStructuredData;
        if (!saved?.id) {
          throw new Error(
            'Failed to persist redaction status SDO: createStructuredData returned empty payload.'
          );
        }
        return normalizeRedactionStatusSdoRecord({
          id: saved.id,
          createdDateTime: saved.createdDateTime,
          modifiedDateTime: saved.modifiedDateTime,
          data: (saved.data || status) as RedactionStatusSdoRecord['data'],
        });
      })
    );

    dispatch(SAVE_REDACTION_STATUSES_SUCCESS({ records, silent }));
  };

  const clearSchemaCache = () => {
    clearRedactionStatusSchemaId(getState);
  };

  const isValidationError = (error: unknown): boolean =>
    error instanceof Error &&
    error.message.toLowerCase().includes('validation errors');

  try {
    const schemaId = await resolveSchemaId();
    if (!schemaId) {
      dispatch(SAVE_REDACTION_STATUSES_FAILURE());
      return;
    }
    await saveWithSchemaId(schemaId);
  } catch (error) {
    if (isValidationError(error)) {
      try {
        clearSchemaCache();
        // Bypass the config pin, or this re-resolves the same stale id.
        const refreshedSchemaId = await resolveSchemaId(true);
        if (!refreshedSchemaId) {
          throw error;
        }
        await saveWithSchemaId(refreshedSchemaId);
        return;
      } catch (retryError) {
        console.error('saveRedactionStatuses retry failed', {
          changedStatusFilter,
          redactionStatuses,
          error: retryError,
        });
        dispatch(SAVE_REDACTION_STATUSES_FAILURE());
        return;
      }
    }
    console.error('saveRedactionStatuses failed', {
      changedStatusFilter,
      redactionStatuses,
      error,
    });
    dispatch(SAVE_REDACTION_STATUSES_FAILURE());
  }
};

// ---------------------------------------------------------------------------
// Custom statuses (VE-24546)
// ---------------------------------------------------------------------------

const getStatusApiAuthorization = (getState: GQLApiGetState) => {
  const oauthToken = selectOAuthToken(getState());
  const sessionToken = selectSessionToken(getState());
  const { apiRoot, graphQLEndpoint, veritoneAppId } =
    getConfig<Window['config']>(getState());
  return {
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    token: sessionToken || oauthToken,
    veritoneAppId,
  };
};

const clearStatusSchemaCache = (getState: GQLApiGetState) => {
  clearRedactionStatusSchemaId(getState);
};

const isStatusValidationError = (error: unknown): boolean =>
  error instanceof Error &&
  error.message.toLowerCase().includes('validation errors');

/**
 * Runs `run(schemaId)`; on a validation error (stale cached id after a
 * republish) clears the cache and retries once. Returns false if no id resolves.
 */
const runWithStatusSchema = async (
  dispatch: GQLApiDispatch,
  getState: GQLApiGetState,
  run: (schemaId: string) => Promise<void>
): Promise<boolean> => {
  const schemaId = await lookupLatestRedactionStatusSchemaId(
    dispatch,
    getState
  );
  if (!schemaId) {
    return false;
  }
  try {
    await run(schemaId);
    return true;
  } catch (error) {
    if (!isStatusValidationError(error)) {
      throw error;
    }
    clearStatusSchemaCache(getState);
    // Bypass the config pin, or this re-resolves the same stale id.
    const refreshedSchemaId = await lookupLatestRedactionStatusSchemaId(
      dispatch,
      getState,
      { ignorePinnedSchemaId: true }
    );
    if (!refreshedSchemaId) {
      throw error;
    }
    await run(refreshedSchemaId);
    return true;
  }
};

const persistCustomStatus = async (
  authorization: ReturnType<typeof getStatusApiAuthorization>,
  status: RedactionCustomStatus,
  schemaId: string
): Promise<void> => {
  const response = await fetchGraphQLApi<SaveRedactionStatusResponse>({
    ...authorization,
    query: saveRedactionStatusQuery,
    variables: {
      id: status.id,
      schemaId,
      data: getRedactionCustomStatusSdoData(status),
    },
  });
  if (response?.errors?.length) {
    const firstError = response.errors[0] as { message?: string } | undefined;
    throw new Error(
      firstError?.message ||
        'Unknown GraphQL error while saving custom redaction status.'
    );
  }
  if (!response?.data?.createStructuredData?.id) {
    throw new Error(
      'Failed to persist custom redaction status SDO: createStructuredData returned empty payload.'
    );
  }
};

/**
 * Upserts only built-in rows whose order differs from `baseline` (avoids
 * rewriting all five). Symmetric: (new, old) to apply, (old, new) to roll back.
 */
const persistBuiltInStatusOrder = async (
  authorization: ReturnType<typeof getStatusApiAuthorization>,
  settings: RedactionStatusSettings,
  baseline: RedactionStatusSettings,
  sdoIdsByStatus: Record<string, string>,
  schemaId: string
): Promise<void> => {
  const baselineOrderByKey = new Map(
    getRedactionStatusSdoData(baseline).map((data) => [
      data.statusKey,
      data.displayOrder,
    ])
  );
  for (const data of getRedactionStatusSdoData(settings)) {
    if (baselineOrderByKey.get(data.statusKey) === data.displayOrder) {
      continue;
    }
    const id = (data.statusKey && sdoIdsByStatus[data.statusKey]) || uuid();
    const response = await fetchGraphQLApi<SaveRedactionStatusResponse>({
      ...authorization,
      query: saveRedactionStatusQuery,
      variables: { id, schemaId, data },
    });
    if (response?.errors?.length) {
      const firstError = response.errors[0] as { message?: string } | undefined;
      throw new Error(
        firstError?.message ||
          'Unknown GraphQL error while saving redaction status order.'
      );
    }
    if (!response?.data?.createStructuredData?.id) {
      throw new Error(
        'Failed to persist redaction status order: createStructuredData returned empty payload.'
      );
    }
  }
};

export const saveRedactionCustomStatus: Thunk<{
  customStatus: RedactionCustomStatus;
  closeDrawer?: boolean;
}> =
  ({ customStatus, closeDrawer }) =>
  async (dispatch, getState) =>
    await enqueueStatusPersist(async () => {
      try {
        const authorization = getStatusApiAuthorization(getState);
        // modifiedBy = current user; createdBy preserved from the existing row.
        const email = userModule.selectUser(getState())?.email ?? '';
        const previous = selectRedactionCustomStatuses(getState()).find(
          (status) => status.id === customStatus.id
        );
        const statusToPersist: RedactionCustomStatus = {
          ...customStatus,
          createdBy: previous?.createdBy || customStatus.createdBy || email,
          modifiedBy: email,
        };
        const ok = await runWithStatusSchema(dispatch, getState, (schemaId) =>
          persistCustomStatus(authorization, statusToPersist, schemaId)
        );
        if (!ok) {
          dispatch(SAVE_REDACTION_CUSTOM_STATUS_FAILURE());
          return;
        }
        dispatch(
          SAVE_REDACTION_CUSTOM_STATUS_SUCCESS({
            statusLabel: statusToPersist.statusLabel,
            closeDrawer,
          })
        );
      } catch (error) {
        console.error('saveRedactionCustomStatus failed', {
          customStatus,
          error,
        });
        dispatch(SAVE_REDACTION_CUSTOM_STATUS_FAILURE());
      }
    });

/**
 * Soft delete (VE-24546 follow-up): writes `isDeleted: true` on the SDO
 * instead of removing the row, so files still tagged with the status keep
 * resolving its attributes (name, color, lock policy). The row disappears
 * from the statuses page and every picker, and its name becomes reusable.
 */
export const deleteRedactionCustomStatus: Thunk<{
  sdoId: string;
  statusLabel: string;
}> =
  ({ sdoId, statusLabel }) =>
  async (dispatch, getState) =>
    await enqueueStatusPersist(async () => {
      try {
        const authorization = getStatusApiAuthorization(getState);
        const email = userModule.selectUser(getState())?.email ?? '';
        const existing = selectRedactionCustomStatuses(getState()).find(
          (status) => status.id === sdoId
        );
        if (!existing) {
          throw new Error(
            'Cannot delete custom redaction status: row not found in store.'
          );
        }
        const ok = await runWithStatusSchema(dispatch, getState, (schemaId) =>
          persistCustomStatus(
            authorization,
            {
              ...existing,
              isDeleted: true,
              ...(email ? { modifiedBy: email } : {}),
            },
            schemaId
          )
        );
        if (!ok) {
          dispatch(DELETE_REDACTION_CUSTOM_STATUS_FAILURE());
          return;
        }
        dispatch(DELETE_REDACTION_CUSTOM_STATUS_SUCCESS({ statusLabel }));
      } catch (error) {
        console.error('deleteRedactionCustomStatus failed', { sdoId, error });
        dispatch(DELETE_REDACTION_CUSTOM_STATUS_FAILURE());
      }
    });

/**
 * Restores a soft-deleted custom status: clears `isDeleted` and hands the row
 * a fresh last-place `displayOrder` — its old slot has no meaning to users
 * who reordered the page since (VE-24546 follow-up). The UI blocks restoring
 * when a live status has since taken the name.
 */
export const restoreRedactionCustomStatus: Thunk<{
  sdoId: string;
  statusLabel: string;
}> =
  ({ sdoId, statusLabel }) =>
  async (dispatch, getState) =>
    await enqueueStatusPersist(async () => {
      try {
        const authorization = getStatusApiAuthorization(getState);
        const email = userModule.selectUser(getState())?.email ?? '';
        const customStatuses = selectRedactionCustomStatuses(getState());
        const existing = customStatuses.find((status) => status.id === sdoId);
        if (!existing) {
          throw new Error(
            'Cannot restore custom redaction status: row not found in store.'
          );
        }
        const displayOrder = getNextCustomStatusDisplayOrder({
          settings: selectRedactionStatusSettings(getState()),
          customStatuses,
        });
        const ok = await runWithStatusSchema(dispatch, getState, (schemaId) =>
          persistCustomStatus(
            authorization,
            {
              ...existing,
              isDeleted: false,
              displayOrder,
              ...(email ? { modifiedBy: email } : {}),
            },
            schemaId
          )
        );
        if (!ok) {
          dispatch(RESTORE_REDACTION_CUSTOM_STATUS_FAILURE());
          return;
        }
        dispatch(RESTORE_REDACTION_CUSTOM_STATUS_SUCCESS({ statusLabel }));
      } catch (error) {
        console.error('restoreRedactionCustomStatus failed', { sdoId, error });
        dispatch(RESTORE_REDACTION_CUSTOM_STATUS_FAILURE());
      }
    });

/**
 * Hard-deletes a soft-deleted custom status's SDO (the "permanently delete"
 * action in the deleted-statuses view). Files still tagged with it fall back
 * to the orphan path: they keep showing the frozen status name from the TDO
 * tag but lose the other attributes. Reuses the DELETE actions so the toast
 * and refetch flow match soft delete.
 */
export const permanentlyDeleteRedactionCustomStatus: Thunk<{
  sdoId: string;
  statusLabel: string;
}> =
  ({ sdoId, statusLabel }) =>
  async (dispatch, getState) =>
    await enqueueStatusPersist(async () => {
      try {
        const authorization = getStatusApiAuthorization(getState);
        const ok = await runWithStatusSchema(
          dispatch,
          getState,
          async (schemaId) => {
            const response =
              await fetchGraphQLApi<DeleteRedactionStatusResponse>({
                ...authorization,
                query: permanentlyDeleteRedactionStatusQuery,
                variables: { sdoId, schemaId },
              });
            if (response?.errors?.length) {
              const firstError = response.errors[0] as
                | { message?: string }
                | undefined;
              throw new Error(
                firstError?.message ||
                  'Unknown GraphQL error while permanently deleting custom redaction status.'
              );
            }
            // Empty payload with no errors must not count as success.
            if (!response?.data?.deleteStructuredData?.id) {
              throw new Error(
                'Failed to permanently delete custom redaction status SDO: deleteStructuredData returned empty payload.'
              );
            }
          }
        );
        if (!ok) {
          dispatch(DELETE_REDACTION_CUSTOM_STATUS_FAILURE());
          return;
        }
        dispatch(DELETE_REDACTION_CUSTOM_STATUS_SUCCESS({ statusLabel }));
      } catch (error) {
        console.error('permanentlyDeleteRedactionCustomStatus failed', {
          sdoId,
          error,
        });
        dispatch(DELETE_REDACTION_CUSTOM_STATUS_FAILURE());
      }
    });

/**
 * Current server rows keyed by SDO id, for merging a reorder onto.
 *
 * A drag owns `displayOrder` and nothing else, but the mutation replaces the
 * whole `data` object — so writing the dragging admin's page-load snapshot
 * would revert a rename or recolor another admin made in the meantime, and the
 * rollback would revert it a second time. Reading first makes both writes a
 * merge (VE-24546).
 *
 * Best-effort: on any failure the caller falls back to its own snapshot, since
 * losing the reorder outright is worse than the narrow clobber window.
 */
const readCurrentCustomStatuses = async (
  authorization: ReturnType<typeof getStatusApiAuthorization>,
  schemaId: string
): Promise<Map<string, RedactionCustomStatus>> => {
  const records: RedactionStatusSdoRecord[] = [];
  for (let page = 0; page < STATUS_FETCH_MAX_PAGES; page += 1) {
    const response = await fetchGraphQLApi<FetchRedactionStatusesResponse>({
      ...authorization,
      query: RETRIEVE_REDACTION_STATUSES,
      variables: {
        schemaId,
        limit: STATUS_FETCH_PAGE_SIZE,
        offset: records.length,
      },
    });
    // Throw rather than return a partial map: silently returning an empty one
    // would send the caller down the snapshot path — the clobber this read
    // exists to prevent — with nothing logged to explain it.
    if (response?.errors?.length) {
      const firstError = response.errors[0] as { message?: string } | undefined;
      throw new Error(
        firstError?.message ||
          'Unknown GraphQL error while reading current redaction statuses.'
      );
    }
    const pageRecords = response?.data?.structuredDataObjects?.records ?? [];
    records.push(...pageRecords);
    // Short page = end of the set; see the note in fetchRedactionStatuses on
    // why this does not key off `count`.
    if (pageRecords.length < STATUS_FETCH_PAGE_SIZE) {
      break;
    }
  }
  return new Map(
    getRedactionCustomStatusesFromRecords(records).map((status) => [
      status.id,
      status,
    ])
  );
};

/**
 * Persist a new status-pool order (VE-24546 #5). Writes are sequential; on any
 * failure every touched row is rolled back (best-effort). Calls are serialized
 * against every other status write via `enqueueStatusPersist`.
 */
export const reorderRedactionStatusPool: Thunk<{
  customStatuses: readonly RedactionCustomStatus[];
  builtInSettings?: RedactionStatusSettings;
  previousCustomStatuses?: readonly RedactionCustomStatus[];
  previousBuiltInSettings?: RedactionStatusSettings;
  sdoIdsByStatus?: Record<string, string>;
}> =
  ({
    customStatuses,
    builtInSettings,
    previousCustomStatuses,
    previousBuiltInSettings,
    sdoIdsByStatus,
  }) =>
  async (dispatch, getState) => {
    const run = async () => {
      try {
        const authorization = getStatusApiAuthorization(getState);
        // Previous values come from the payload snapshot (REORDER_REQUEST already
        // optimistically overwrote the store), not getState().
        const previousById = new Map(
          (
            previousCustomStatuses ?? selectRedactionCustomStatuses(getState())
          ).map((status) => [status.id, status])
        );
        const previousSettings =
          previousBuiltInSettings ?? selectRedactionStatusSettings(getState());
        const ids = sdoIdsByStatus ?? {};

        const ok = await runWithStatusSchema(
          dispatch,
          getState,
          async (schemaId) => {
            const appliedCustoms: RedactionCustomStatus[] = [];
            // Freshest server copy of each row, so both the write and its
            // rollback change only displayOrder (VE-24546).
            let currentById = new Map<string, RedactionCustomStatus>();
            try {
              currentById = await readCurrentCustomStatuses(
                authorization,
                schemaId
              );
            } catch (readError) {
              console.error(
                'reorderRedactionStatusPool could not read current rows; falling back to the local snapshot',
                { readError }
              );
            }
            /** The row as the server has it, carrying `displayOrder`. */
            const withOrder = (status: RedactionCustomStatus) => ({
              ...(currentById.get(status.id) ?? status),
              displayOrder: status.displayOrder,
            });
            try {
              // Built-ins first so a mixed reorder lands in one refetch.
              if (builtInSettings) {
                await persistBuiltInStatusOrder(
                  authorization,
                  builtInSettings,
                  previousSettings,
                  ids,
                  schemaId
                );
              }
              for (const status of customStatuses) {
                const previous = previousById.get(status.id);
                if (previous && previous.displayOrder === status.displayOrder) {
                  continue;
                }
                await persistCustomStatus(
                  authorization,
                  withOrder(status),
                  schemaId
                );
                appliedCustoms.push(status);
              }
            } catch (error) {
              // Roll back written customs, then restore built-in order.
              for (const status of appliedCustoms) {
                const previous = previousById.get(status.id);
                if (!previous) {
                  continue;
                }
                try {
                  await persistCustomStatus(
                    authorization,
                    withOrder(previous),
                    schemaId
                  );
                } catch (rollbackError) {
                  console.error(
                    'reorderRedactionStatusPool custom rollback failed',
                    {
                      statusId: status.id,
                      rollbackError,
                    }
                  );
                }
              }
              if (builtInSettings) {
                try {
                  await persistBuiltInStatusOrder(
                    authorization,
                    previousSettings,
                    builtInSettings,
                    ids,
                    schemaId
                  );
                } catch (rollbackError) {
                  console.error(
                    'reorderRedactionStatusPool built-in rollback failed',
                    { rollbackError }
                  );
                }
              }
              throw error;
            }
          }
        );
        if (!ok) {
          dispatch(REORDER_REDACTION_CUSTOM_STATUSES_FAILURE());
          return;
        }
        dispatch(REORDER_REDACTION_CUSTOM_STATUSES_SUCCESS());
      } catch (error) {
        console.error('reorderRedactionStatusPool failed', { error });
        dispatch(REORDER_REDACTION_CUSTOM_STATUSES_FAILURE());
      }
    };
    await enqueueStatusPersist(run);
  };
